/**
 * Helper functions for Compare and Wishlist functionality
 * This file contains utilities to dispatch events when items are added/removed
 */

/**
 * Dispatches a custom event when compare items are updated
 */
export const dispatchCompareUpdate = () => {
  const event = new Event('compareUpdated');
  window.dispatchEvent(event);
};

/**
 * Dispatches a custom event when wishlist items are updated
 */
export const dispatchWishlistUpdate = () => {
  const event = new Event('wishlistUpdated');
  window.dispatchEvent(event);
};

/**
 * Adds an item to the compare list
 * @param {Object} product - The product to add to compare
 * @returns {boolean} - Whether the item was added (true) or removed (false)
 */
export const toggleCompareItem = (product) => {
  try {
    const existingCompareList = localStorage.getItem('compareItems');
    let compareList = [];
    
    if (existingCompareList) {
      try {
        compareList = JSON.parse(existingCompareList);
        if (!Array.isArray(compareList)) compareList = [];
      } catch (error) {
        console.error("Error parsing compare list:", error);
      }
    }
    
    const existingIndex = compareList.findIndex(item => item.id === product.id);
    let isAdded = false;
    
    if (existingIndex === -1) {
      // Add to compare
      compareList.push({
        id: product.id,
        name: product.name,
        price: product.price || 0,
        imageUrl: product.imageUrl || product.image,
        image: product.imageUrl || product.image,
        description: product.description || '',
        deliverySpeed: product.deliverySpeed || 'normal'
      });
      isAdded = true;
    } else {
      // Remove from compare
      compareList.splice(existingIndex, 1);
      isAdded = false;
    }
    
    localStorage.setItem('compareItems', JSON.stringify(compareList));
    dispatchCompareUpdate();
    return isAdded;
  } catch (error) {
    console.error("Error updating compare list:", error);
    return false;
  }
};

/**
 * Adds an item to the wishlist
 * @param {Object} product - The product to add to wishlist
 * @returns {boolean} - Whether the item was added (true) or removed (false)
 */
export const toggleWishlistItem = (product) => {
  try {
    const existingWishList = localStorage.getItem('wishListItems');
    let wishList = [];
    
    if (existingWishList) {
      try {
        wishList = JSON.parse(existingWishList);
        if (!Array.isArray(wishList)) wishList = [];
      } catch (error) {
        console.error("Error parsing wish list:", error);
      }
    }
    
    const existingIndex = wishList.findIndex(item => item.id === product.id);
    let isAdded = false;
    
    if (existingIndex === -1) {
      // Add to wishlist
      wishList.push({
        id: product.id,
        name: product.name,
        price: product.price || 0,
        imageUrl: product.imageUrl || product.image,
        image: product.imageUrl || product.image,
        description: product.description || '',
        deliverySpeed: product.deliverySpeed || 'normal'
      });
      isAdded = true;
    } else {
      // Remove from wishlist
      wishList.splice(existingIndex, 1);
      isAdded = false;
    }
    
    localStorage.setItem('wishListItems', JSON.stringify(wishList));
    dispatchWishlistUpdate();
    return isAdded;
  } catch (error) {
    console.error("Error updating wish list:", error);
    return false;
  }
};

/**
 * Checks if a product is in the compare list
 * @param {string} productId - The product ID to check
 * @returns {boolean} - Whether the product is in the compare list
 */
export const isProductInCompare = (productId) => {
  try {
    const existingCompareList = localStorage.getItem('compareItems');
    if (!existingCompareList) return false;
    
    const compareList = JSON.parse(existingCompareList);
    if (!Array.isArray(compareList)) return false;
    
    return compareList.some(item => item.id === productId);
  } catch (error) {
    console.error("Error checking compare status:", error);
    return false;
  }
};

/**
 * Checks if a product is in the wishlist
 * @param {string} productId - The product ID to check
 * @returns {boolean} - Whether the product is in the wishlist
 */
export const isProductInWishlist = (productId) => {
  try {
    const existingWishList = localStorage.getItem('wishListItems');
    if (!existingWishList) return false;
    
    const wishList = JSON.parse(existingWishList);
    if (!Array.isArray(wishList)) return false;
    
    return wishList.some(item => item.id === productId);
  } catch (error) {
    console.error("Error checking wishlist status:", error);
    return false;
  }
};